import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
/* =========================================================================  */
/* Context(s) */
/* =========================================================================  */

/**
 * @type {import('$types/components/contexts.d.ts').LocaleContext}
 */
const LocaleContext = createContext({
    currentLocale: null,
    locales: {},
    setCurrentLocale: () => { },
    translate: () => { },
    translations: [],
    translationPages: []
});

/* =========================================================================  */
/* Hook(s) */
/* =========================================================================  */

/**
 * @hook useLocale
 * @type {import('$types/components/contexts.d.ts').useLocale}
 */
const useLocale = () => {
    const context = useContext(LocaleContext);

    if (context === undefined) {
        throw new Error('useLocale must be used within a LocaleProvider.');
    }

    return context;
};

/* =========================================================================  */
/* Provider(s) */
/* =========================================================================  */

/**
 * @component LocaleProvider
 * @type {import('$types/components/contexts.d.ts').LocaleProvider}
 */
const LocaleProvider = ({
    children,
    current,
    locales = [],
    translations,
    translationPages
}) => {
    const [currentLocale, setCurrentLocale] = useState(current);
    const [debugMode, setDebugMode] = useState(false);

    useEffect(() => {
        const keyHandler = (e) => {
            if (e.ctrlKey && e.shiftKey && String(e.key).toUpperCase() === 'D') {
                setDebugMode((v) => !v);
            }
        };

        document.addEventListener('keydown', keyHandler);
        return () => document.removeEventListener('keydown', keyHandler);
    }, []);

    /**
     * @param {string} term translatable term
     * @param {Record<string, string>} variables variables to populate
     * @param {'PlainText' | 'RichText'} mode
     * @returns {string}
     */
    const translate = (term, variables, mode = 'PlainText') => {
        if (['PlainText', 'RichText'].indexOf(mode) === -1) {
            throw new Error('Invalid translation mode.');
        }

        let translatedTerm = term || '';

        translations.some((entry) => {
            if (entry.key === term) {
                if (debugMode) {
                    translatedTerm = term;
                } else {
                    const localePrefix = currentLocale.code.toLowerCase().replace(
                        /([-_][a-z])/g,
                        (group) => group.toUpperCase().substring(1)
                    );
                    const value = entry[`${localePrefix}${mode}`] || entry[`enEn${mode}`];
                    if (variables) {
                        translatedTerm = value.replace(/{(.*?)}/g, (match, offset) => variables[offset]);
                    } else {
                        translatedTerm = value;
                    }
                }
            }
        });

        return translatedTerm;
    };

    return (
        <LocaleContext.Provider value={useMemo(() => {
            return {
                currentLocale,
                locales,
                setCurrentLocale,
                translate,
                translations,
                translationPages
            };
        }, [
            currentLocale,
            locales,
            setCurrentLocale,
            translate,
            translations,
            translationPages
        ])}>
            {children}
        </LocaleContext.Provider>
    );
};

/* =========================================================================  */
/* Export(s) */
/* =========================================================================  */

export {
    LocaleProvider,
    useLocale
};
